import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Camera, Lightbulb, ArrowRight, CheckCircle2, Store, HelpCircle } from "lucide-react";
import { Generator } from "@/components/Generator";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getSeoPage } from "@/lib/api";

export default function SeoLandingPage() {
  const { seoSlug } = useParams();
  const slug = (seoSlug || "").replace(/-anzeige-erstellen$/, "");
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    getSeoPage(slug).then(setPage).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
        <Seo title="Seite nicht gefunden: KleinanzeigenGenerator" description="Diese Seite wurde nicht gefunden." />
        <h1 className="text-2xl font-semibold">Seite nicht gefunden</h1>
        <p className="text-muted-foreground mt-2">Diese Kategorie gibt es nicht.</p>
        <Link to="/" className="text-primary underline mt-4 inline-block">Zur Startseite</Link>
      </div>
    );
  }

  if (!page) {
    return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center text-muted-foreground">Laden …</div>;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}${page.urlPath}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (page.faq || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: origin || undefined },
        { "@type": "ListItem", position: 2, name: page.group },
        { "@type": "ListItem", position: 3, name: page.h1, item: pageUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: `${page.name}-Anzeige erstellen`,
      description: page.intro,
      step: [
        { "@type": "HowToStep", position: 1, name: "Foto hochladen", text: `Lade 1 bis 2 Fotos von deinem ${page.name} hoch.` },
        { "@type": "HowToStep", position: 2, name: "Angaben machen", text: "Gib kurz an, was du verkaufst, und beantworte bis zu 3 Fragen." },
        { "@type": "HowToStep", position: 3, name: "Text kopieren", text: "Kopiere den fertigen Verkaufstext für deine Plattform." },
      ],
    },
  ];

  return (
    <div>
      <Seo title={page.metaTitle} description={page.metaDescription} canonical={pageUrl} />
      <JsonLd data={jsonLd} />

      {/* Hero + Generator */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.08), rgba(249,115,22,0.06), rgba(251,250,247,0))", height: "50%" }} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6 relative">
          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-8">
            <div>
              <Badge variant="secondary" className="mb-3">{page.group}</Badge>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{page.h1}</h1>
              <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">{page.intro}</p>
              <div className="mt-6">
                <Generator presetCategory={page.category} presetTitlePlaceholder={`z. B. ${page.name} …`} />
              </div>
            </div>
            <div className="hidden lg:block"><AdSlot variant="rail" /></div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:hidden mt-4"><AdSlot variant="inline" /></div>

      {/* What to include + selling tips */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Das gehört in deine {page.name}-Anzeige</h2>
          <p className="text-muted-foreground mt-2 mb-4">Diese Angaben erwarten Käufer, der Generator deckt sie automatisch ab:</p>
          <ul className="space-y-2.5">
            {page.whatToInclude.map((it, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base">{it}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">{page.name} verkaufen: Darauf solltest du achten</h2>
          <div className="mt-4 space-y-4">
            {page.sellingTips.map((p, i) => (
              <p key={i} className="text-sm sm:text-base text-muted-foreground leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Example ad + typical questions */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 grid md:grid-cols-2 gap-6">
        <Card className="p-5 border" data-testid="seo-example-ad">
          <div className="flex items-center gap-2 mb-2"><Camera className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Beispiel-Anzeige</h3></div>
          <p className="font-medium text-sm">{page.exampleAd.title}</p>
          <p className="prose-listing text-sm text-muted-foreground mt-2">{page.exampleAd.description}</p>
        </Card>
        <Card className="p-5 border">
          <div className="flex items-center gap-2 mb-2"><HelpCircle className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Typische Fragen im Generator</h3></div>
          <ul className="mt-2 space-y-2">
            {page.typicalQuestions.map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{q}</li>
            ))}
          </ul>
        </Card>
      </section>

      {/* Photo tips + platform note */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 grid md:grid-cols-2 gap-6">
        <Card className="p-5 border" data-testid="seo-photo-tips">
          <div className="flex items-center gap-2 mb-3"><Lightbulb className="h-4 w-4 text-accent" /><h3 className="font-heading font-semibold">Foto-Tipps für deine {page.name}-Anzeige</h3></div>
          <div className="space-y-2">
            {page.photoTips.map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />{t}</div>
            ))}
          </div>
        </Card>
        <Card className="p-5 border bg-secondary/40" data-testid="seo-platform-note">
          <div className="flex items-center gap-2 mb-3"><Store className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Wo verkaufe ich {page.name} am besten?</h3></div>
          <p className="text-sm text-muted-foreground leading-relaxed">{page.platformNote}</p>
          <p className="text-sm text-muted-foreground leading-relaxed mt-2">Der Generator erstellt dir passende Varianten für Kleinanzeigen, Facebook Marketplace, Vinted und allgemeine Plattformen, mit einem Klick.</p>
        </Card>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6"><AdSlot variant="inline" /></div>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4">Häufige Fragen zur {page.name}-Anzeige</h2>
        <Faq items={page.faq} />
      </section>

      {/* Internal links */}
      {page.related?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-lg font-semibold mb-3">Weitere Kategorien</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {page.related.map((rs) => (
              <Link key={rs} to={`/${rs}-anzeige-erstellen`} data-testid={`related-link-${rs}`}>
                <Card className="p-3 border hover:shadow-md transition-shadow flex items-center justify-between gap-2">
                  <span className="text-sm font-medium capitalize">{rs.replace(/-/g, " ")}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
